/**
 * Local-first hooks for COHORTS and PROGRAMS (F2-A). Data arrives via the
 * per-role streams (my_cohorts_student/teacher, campus/org_admin_scope,
 * academy_org for programs and open cohorts); writes go through the
 * write-path or the REST /api/cohorts endpoints.
 */
import type { Accessor } from "solid-js";
import {
  cohortQueryKeys,
  type CohortRosterRow,
  type CohortTeacherProfileRow,
  getCohortGroupMembersQuery,
  getCohortGroupsQuery,
  getCohortModuleSettingsQuery,
  getCohortQuery,
  getCohortRosterQuery,
  getCohortsByOrgQuery,
  getMyCohortEnrollmentsQuery,
  getOpenCohortsByCourseQuery,
  getProgramCoursesQuery,
  getProgramQuery,
  getProgramsByOrgQuery,
} from "@space/shared";
import { createPowerSyncQuery, createPowerSyncSingle } from "@space/shared/solid";
import type {
  Cohort,
  CohortEnrollment,
  CohortGroup,
  CohortGroupMember,
  CohortModuleSetting,
  Program,
  ProgramCourse,
} from "@space/powersync";
import { fetchOrgCourses, type OrgCourseEntry } from "@/features/academy/api/org-courses";
import { api, createApiQuery, createSafeQuery } from "@/lib/api";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";

function useBase() {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { userId } = useSession();
  return { db, drizzle, userId, staleTime: OFFLINE_CACHE_STALE_TIME_MS };
}

export function useCohortsByOrg(orgId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<Cohort>({
    queryKey: () => cohortQueryKeys.byOrg(orgId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = orgId();
      return db && id ? getCohortsByOrgQuery(db, id) : null;
    },
    enabled: () => Boolean(orgId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

export function useCohort(cohortId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncSingle<Cohort>({
    queryKey: () => cohortQueryKeys.byId(cohortId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = cohortId();
      return db && id ? getCohortQuery(db, id) : null;
    },
    enabled: () => Boolean(cohortId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** OPEN cohorts of a course in my org (enrollment picker). */
export function useOpenCohorts(
  orgId: Accessor<string | undefined>,
  courseId: Accessor<string | undefined>,
) {
  const base = useBase();
  return createPowerSyncQuery<Cohort>({
    queryKey: () => cohortQueryKeys.openByCourse(orgId(), courseId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const org = orgId();
      const course = courseId();
      return db && org && course ? getOpenCohortsByCourseQuery(db, org, course) : null;
    },
    enabled: () => Boolean(orgId()) && Boolean(courseId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** MY cohort enrollments. */
export function useMyCohortEnrollments() {
  const base = useBase();
  return createPowerSyncQuery<CohortEnrollment>({
    queryKey: () => cohortQueryKeys.myEnrollments(base.userId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const uid = base.userId();
      return db && uid ? getMyCohortEnrollmentsQuery(db, uid) : null;
    },
    enabled: () => Boolean(base.userId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

export function useCohortRoster(cohortId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<CohortRosterRow>({
    queryKey: () => cohortQueryKeys.roster(cohortId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = cohortId();
      return db && id ? getCohortRosterQuery(db, id) : null;
    },
    enabled: () => Boolean(cohortId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/**
 * PUBLIC teacher records for a cohort (1C · classroom landing page).
 *
 * E4 · plan 06: via REST (`GET /api/cohorts/:id/teachers`). It used to be a
 * local join that depended on `academy_org` replicating EVERY public record
 * in the academy to EVERY member just to render a classroom's two teachers.
 * This isn't identity: it's the data the teacher themselves chose to
 * publish; name and email still come from the roster, which doesn't answer
 * to a student.
 */
export function useCohortTeacherProfiles(cohortId: Accessor<string | undefined>) {
  return createApiQuery<CohortTeacherProfileRow[], unknown>(
    () => cohortQueryKeys.teacherProfiles(cohortId()),
    async () =>
      api.GET("/api/cohorts/{id}/teachers", {
        params: { path: { id: cohortId() ?? "" } },
      }),
    () => ({ enabled: Boolean(cohortId()), staleTime: OFFLINE_CACHE_STALE_TIME_MS }),
  );
}

// D3 (plan 06 · E3): `useCohortTeachers` has been DELETED along with its
// Drizzle query. It was the cohort's second identity join and no screen
// called it; the cohort's teachers now come in the same response as the
// roster (`useCohortIdentities`).

export function useCohortGroups(cohortId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<CohortGroup>({
    queryKey: () => cohortQueryKeys.groups(cohortId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = cohortId();
      return db && id ? getCohortGroupsQuery(db, id) : null;
    },
    enabled: () => Boolean(cohortId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

export function useCohortGroupMembers(cohortId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<CohortGroupMember>({
    queryKey: () => cohortQueryKeys.groupMembers(cohortId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = cohortId();
      return db && id ? getCohortGroupMembersQuery(db, id) : null;
    },
    enabled: () => Boolean(cohortId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

export function useCohortModuleSettings(cohortId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<CohortModuleSetting>({
    queryKey: () => cohortQueryKeys.moduleSettings(cohortId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = cohortId();
      return db && id ? getCohortModuleSettingsQuery(db, id) : null;
    },
    enabled: () => Boolean(cohortId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/**
 * Courses published in the org (cohort/program picker) — **REST-only path**.
 *
 * Plan 06 · E1 (closes D2), restrictive commit: the `org_courses` stream no
 * longer exists, so the local branch and the flag that switched between the
 * two sources disappeared with it. What's left is
 * `GET /api/academy/:orgId/courses`, which filters `deleted_at`, respects
 * publication status and requires `ManageAcademics` for drafts — a scope
 * that sync rules can't express.
 *
 * The type is the DTO generated from Rust, with no hand-written intermediate
 * row: the `{id, title, link_id}` mapping only existed to mimic the shape of
 * the local query that was just deleted.
 */
export function useOrgCourses(orgId: Accessor<string | undefined>) {
  const base = useBase();
  return createSafeQuery(() => ({
    queryKey: cohortQueryKeys.orgCoursesRest(orgId()),
    enabled: Boolean(orgId()),
    staleTime: base.staleTime,
    queryFn: (): Promise<OrgCourseEntry[]> => fetchOrgCourses(orgId() ?? ""),
  }));
}

// ─── Programs ────────────────────────────────────────────────────────────────

export function useProgramsByOrg(orgId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<Program>({
    queryKey: () => cohortQueryKeys.programs(orgId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = orgId();
      return db && id ? getProgramsByOrgQuery(db, id) : null;
    },
    enabled: () => Boolean(orgId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

export function useProgram(programId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncSingle<Program>({
    queryKey: () => cohortQueryKeys.programById(programId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = programId();
      return db && id ? getProgramQuery(db, id) : null;
    },
    enabled: () => Boolean(programId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

export function useProgramCourses(programId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<ProgramCourse>({
    queryKey: () => cohortQueryKeys.programCourses(programId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = programId();
      return db && id ? getProgramCoursesQuery(db, id) : null;
    },
    enabled: () => Boolean(programId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}
